import {
  DEFAULT_DATE_FORMAT_FNS,
  FilterKeys,
  GetSignUpsListQueryFilters,
  SignUpStatuses,
  SignUpType,
} from '@schooly/api';
import { EventsInvite } from '@schooly/constants';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

export type UseSignUpsFiltersProps = {
  filters: GetSignUpsListQueryFilters;
  onSetFilters: (filters: GetSignUpsListQueryFilters) => void;
};

export const useSignUpsFilters = ({ filters, onSetFilters }: UseSignUpsFiltersProps) => {
  const { formatMessage } = useIntl();

  const actions = useMemo(() => {
    const clearFilter = (key: keyof typeof filters) => () => {
      onSetFilters({
        ...filters,
        [key]: [],
      });
    };

    const onSetSignUpStatus = (v: SignUpStatuses[]) => {
      onSetFilters({
        ...filters,
        [FilterKeys.SignUpStatus]: v,
      });
    };

    const onSetSignUpType = (v: SignUpType[]) => {
      onSetFilters({
        ...filters,
        [FilterKeys.SignUpType]: v,
      });
    };

    const onSetInviteType = (v: EventsInvite[]) => {
      onSetFilters({
        ...filters,
        [FilterKeys.InviteType]: v,
      });
    };

    return {
      onSetDate: (v: [Date, Date]) =>
        onSetFilters({
          ...filters,
          [FilterKeys.Date]: [
            format(v[0], DEFAULT_DATE_FORMAT_FNS),
            format(v[1], DEFAULT_DATE_FORMAT_FNS),
          ],
        }),
      onSetSignUpStatus,
      onClearSignUpStatus: clearFilter(FilterKeys.SignUpStatus),
      onSetSignUpType,
      onClearSignUpType: clearFilter(FilterKeys.SignUpType),
      onSetInviteType,
      onClearInviteType: clearFilter(FilterKeys.InviteType),
    };
  }, [filters, onSetFilters]);

  const filtersDate = filters.date;
  const filtersSignUpStatus = filters.sign_up_status;
  const filtersSignUpType = filters.sign_up_type;
  const filtersInviteType = filters.invite_type;

  const labels = {
    [FilterKeys.Date]: formatMessage({ id: 'schoolProperty-DateAndPeriod' }),
    [FilterKeys.SignUpStatus]: formatMessage({ id: 'eventSignUps-SignUpStatus' }),
    [FilterKeys.SignUpType]: formatMessage({ id: 'eventSignUps-SignUpType' }),
    [FilterKeys.InviteType]: formatMessage({ id: 'eventSignUps-Participation' }),
  };

  return {
    ...actions,
    labels,
    filtersDate,
    filtersSignUpStatus,
    filtersSignUpType,
    filtersInviteType,
  };
};
